import React, { useEffect, useRef } from 'react';
import { SectionHeading } from '../SectionHeading';
import { startingDataColumns, startingDataNote, startingDataRows } from '../../data/startingData';
export function StartingData() {
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          node.setAttribute('data-analytics-viewed', 'true');
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.25
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <section id="starting-data" ref={sectionRef} aria-labelledby="starting-data-heading" data-analytics="starting-data-view" className="w-full bg-warm" data-id="element-302" data-mp="18g9iu30b8zfl">
      <div className="mx-auto max-w-shell px-5 py-16 lg:px-10 lg:py-24" data-id="element-303" data-mp="13nq5n75jc6bb">
        <SectionHeading id="starting-data-heading" eyebrow="REFERENCE DATA / TECHNICAL APPROVAL REQUIRED" heading="Legacy-published starting data" intro="These values are transcribed from Robertson Precision’s legacy website and have not been revalidated for this redesign. Do not use them until Robertson Precision confirms the products, source conditions and values. Actual limits depend on machine horsepower, workholding, part rigidity, tool overhang, coolant, material condition and geometry." data-id="element-304" data-mp="2mt9ryv21q1s4" />

        {/* Desktop / tablet: semantic table */}
        <div className="mt-12 hidden border border-rule bg-white md:block" data-id="element-305" data-mp="1u5nemv30nis2">
          <table className="w-full border-collapse text-left" data-id="element-306" data-mp="1z3owxdsmbe2h">
            <caption className="sr-only" data-id="element-307" data-mp="3oi3jbrg5yout">
              Legacy-published Shear Geometry starting data by material and
              application. Values require Robertson Precision technical
              approval before use.
            </caption>
            <thead data-id="element-308" data-mp="3s1cbxte3m09n">
              <tr className="bg-navy" data-id="element-309" data-mp="3rxoj6wgh9hvz">
                {startingDataColumns.map(col => <th key={col} scope="col" className="border-r border-white/10 px-4 py-3.5 font-mono text-[10px] font-medium tracking-label text-aluminum last:border-r-0" data-id="element-310" data-mp="3n2rchw6o7km9">
                    {col.toUpperCase()}
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-311" data-mp="9n353dskdjk5">
              {startingDataRows.map(row => <tr key={row.material} className="border-t border-rule align-top" data-id="element-312" data-mp="4ujpyce8agd8">
                  <th scope="row" className="border-r border-rule px-4 py-4 text-[14px] font-semibold text-navy" data-id="element-313" data-mp="3w0tkivotndj1">
                    {row.material}
                  </th>
                  <td className="border-r border-rule px-4 py-4 font-mono text-[12.5px] leading-relaxed text-graphite" data-id="element-314" data-mp="sit894c8glzm">
                    {row.insert}
                  </td>
                  <td className="rp-tabular border-r border-rule px-4 py-4 font-mono text-[12.5px] leading-relaxed text-graphite" data-id="element-315" data-mp="14v5dxdgwi88k">
                    {row.speed}
                  </td>
                  <td className="rp-tabular border-r border-rule px-4 py-4 font-mono text-[12.5px] leading-relaxed text-graphite" data-id="element-316" data-mp="1etukwb79e8a1">
                    {row.doc}
                  </td>
                  <td className="rp-tabular border-r border-rule px-4 py-4 font-mono text-[12.5px] leading-relaxed text-graphite" data-id="element-317" data-mp="1nsoedspamijd">
                    {row.feed}
                  </td>
                  <td className="px-4 py-4 text-[13px] leading-relaxed text-graphite/80" data-id="element-318" data-mp="1xk2mupz82son">
                    {row.source}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Mobile: labeled stacked records */}
        <div className="mt-10 space-y-5 md:hidden" data-id="element-319" data-mp="3493vj6obkazw">
          {startingDataRows.map(row => <article key={row.material} aria-label={row.material} className="border border-rule bg-white" data-id="element-320" data-mp="2za0vk0n74uys">
              <h3 className="border-b border-rule bg-navy px-4 py-3 text-[14px] font-semibold text-white" data-id="element-321" data-mp="3dy3yt29utsjw">
                {row.material}
              </h3>
              <dl className="divide-y divide-rule" data-id="element-322" data-mp="2l9g8ute5uf00">
                {[['Insert style', row.insert], ['Speed', row.speed], ['DOC per side', row.doc], ['Feed', row.feed], ['Original source condition', row.source]].map(([label, value]) => <div key={label} className="px-4 py-3" data-id="element-323" data-mp="2qbfe68tgnlzl">
                    <dt className="font-mono text-[9.5px] tracking-label text-copper" data-id="element-324" data-mp="9jnpb0ucqqbc">
                      {String(label).toUpperCase()}
                    </dt>
                    <dd className="rp-tabular mt-1.5 font-mono text-[13px] leading-relaxed text-graphite" data-id="element-325" data-mp="d2miypp9kk8c">
                      {value}
                    </dd>
                  </div>)}
              </dl>
            </article>)}
        </div>

        <p className="rp-measure mt-8 border-l-2 border-copper pl-5 text-[13.5px] leading-[1.7] text-graphite/85" data-id="element-326" data-mp="369o3ohzg0c0">
          {startingDataNote}
        </p>
      </div>
    </section>;
}